<template>
  <div v-if="collectible">
    <v-container v-if="!params.showPackOpening && !params.showRevealedPack">
      <v-row justify="center" align="center">
        <v-col :cols="5" align="right">
          <div>
            <div class="collectibleArtist">
              {{ collectible.artist }}
            </div>
            <div class="collectibleName">
              {{ isPremium ? collectible.premium.name : collectible.name }}
            </div>
            <div class="collectibleDescription">
              {{
                isPremium
                  ? collectible.premium.packDescription
                  : collectible.packDescription
              }}
            </div>
          </div>
          <div v-if="isPremium">
            <v-row>
              <template v-for="option in collectible.premium.purchaseOptions">
                <v-col :key="option.price" cols="6">
                  <div
                    :class="
                      purchaseOption === option
                        ? 'selectedPurchaseOption'
                        : 'unselectedPurchaseOption'
                    "
                    @click="purchaseOption = option"
                  >
                    <div>{{ option.price }} GM POINTS</div>
                    <div>
                      {{ option.packs * (collectible.cardsPerPack || 2) }} CARDS
                    </div>
                  </div>
                </v-col>
              </template>
            </v-row>
          </div>
          <div v-else>
            <div class="freeText">
              FREE
            </div>
            <div class="quantityText">Quantity: {{ remainingQuantity }}</div>
          </div>

          <v-btn
            v-if="new Date(collectible.startDate) > Date.now()"
            class="yellowButton claimButton"
            disabled
          >
            Available Soon
          </v-btn>
          <div v-else-if="isPremium">
            <v-btn
              v-if="!user && !authenticated"
              class="yellowButton claimButton"
              depressed
              text
              @click="$router.push(`/auth?source=${$route.path}`)"
            >
              Sign in
            </v-btn>
            <v-btn
              v-else-if="
                purchaseOption && user.user.points < purchaseOption.price
              "
              class="yellowButton claimButton"
              depressed
              text
              :disabled="!purchaseOption"
              :loading="params.claimingPack || params.loading"
              @click="$router.push('/shop')"
            >
              Get More GM Points
            </v-btn>
            <v-btn
              v-else
              class="yellowButton claimButton"
              depressed
              text
              :disabled="!purchaseOption"
              :loading="params.claimingPack || params.loading"
              @click="$emit('purchase', purchaseOption)"
            >
              Buy Now
              <span v-if="purchaseOption" class="ml-2">
                {{ `(${purchaseOption.price} GM Points)` }}
              </span>
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              v-if="!user && !authenticated"
              class="yellowButton claimButton"
              depressed
              text
              @click="$router.push(`/auth?source=${$route.path}`)"
            >
              Get Your Free Pack
            </v-btn>
            <v-btn
              v-else-if="remainingQuantity > 0 && status"
              class="yellowButton claimButton"
              depressed
              text
              :disabled="remainingQuantity < 1 || !status.claimable"
              :loading="params.claimingPack || params.loading"
              @click="$emit('claim')"
            >
              {{ status.claimable ? 'Pick Your Free Cards' : 'Claimed' }}
            </v-btn>
          </div>
        </v-col>
        <v-col cols="7">
          <v-img
            :src="
              isPremium
                ? collectible.premium.dropImageUrl
                : collectible.dropImageUrl
            "
            eager
          />
        </v-col>
      </v-row>
      <v-row style="margin-top: 40px;">
        <v-col cols="12">
          <div class="sectionTitle">
            Moments in this Roll
          </div>
        </v-col>
        <template v-for="(moment, index) in moments">
          <v-col :key="index" cols="2">
            <video
              :poster="isPremium ? moment.foilImageUrl : moment.imageUrl"
              class="videoFrame"
              autoplay
              muted
              loop
            >
              <source :src="moment.videoUrl" type="video/webm" />
              Your browser does not support the video tag.
            </video>
          </v-col>
        </template>
      </v-row>
      <v-row style="margin-top: 80px">
        <v-col cols="12">
          <div class="sectionTitle">
            GM Pack Guideline:
          </div>
          <div class="guideText">
            <b>About The Pack</b>
          </div>
          <div class="guideText">
            {{
              isPremium
                ? collectible.premium.guidelines
                : collectible.guidelines
            }}
          </div>
        </v-col>
        <v-col cols="12" style="height: 120px;" />
      </v-row>
    </v-container>
    <div v-else-if="params.showPackOpening">
      <video class="videoFrame" autoplay muted @ended="$emit('revealPack')">
        <source :src="PackVideo" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
    <v-container v-else-if="params.showRevealedPack">
      <v-row justify="center">
        <v-col cols="12" style="height: 120px;" />
        <v-col cols="8" justify="center">
          <v-row justify="center">
            <v-col cols="12">
              <div class="pageTitle" style="text-align: center">
                Reveal Collectibles
              </div>
              <div
                class="pageSubtitle"
                style="text-align: center; margin-top: 24px;"
              >
                You can review revealed digital collectibles on your collection
                page.
              </div>
            </v-col>
            <v-col cols="12" style="height: 120px;" />
            <v-col cols="12">
              <v-row>
                <template v-for="(token, index) in revealedTokens">
                  <v-col :key="index" cols="6">
                    <v-card>
                      <video
                        :poster="
                          token.isPremium
                            ? token.moment.foilImageUrl
                            : token.moment.imageUrl
                        "
                        class="videoFrame"
                        autoplay
                        muted
                        loop
                      >
                        <source
                          :src="token.moment.videoUrl"
                          type="video/webm"
                        />
                        Your browser does not support the video tag.
                      </video>
                    </v-card>
                  </v-col>
                </template>
              </v-row>
            </v-col>
            <v-col cols="12" justify="center" align="center">
              <v-btn
                class="yellowButton mx-2"
                depressed
                text
                style="width: 200px; height: 64px; margin-top: 80px;"
                @click="$router.push('/challenges')"
              >
                Challenge
              </v-btn>
              <v-btn
                class="blackButton mx-2"
                depressed
                text
                style="width: 200px; height: 64px; margin-top: 80px;"
                @click="$router.push('/collection')"
              >
                My Collection
              </v-btn>
            </v-col>
            <!-- <v-col cols="12" justify="center" align="center">
              <v-btn
                class="button mx-2"
                style="width: 200px; height: 64px; margin-top: 80px;"
                @click="$emit('backToDrops')"
              >
                Back to Drops
              </v-btn>
            </v-col> -->
          </v-row>
        </v-col>
        <v-col cols="12" style="height: 90px;" />
      </v-row>
    </v-container>
  </div>
  <v-container v-else class="fillHeight" />
</template>

<script>
  import { mapState } from 'vuex';

  export default {
    components: {},

    props: [
      'authenticated',
      'collectible',
      'status',
      'revealedTokens',
      'PackVideo',
      'isPremium',
      'params',
    ],

    data: () => {
      return { purchaseOption: null };
    },

    computed: {
      ...mapState({
        user: (state) => state.user,
      }),
      moments() {
        if (this.isPremium) {
          return this.collectible.moments.filter((moment) => !moment.freeOnly);
        } else {
          return this.collectible.moments.filter(
            (moment) => !moment.premiumOnly
          );
        }
      },
      remainingQuantity() {
        if (!this.collectible || !this.collectible.dailyFreeQuantity) {
          return 0;
        }
        return (
          this.collectible.dailyFreeQuantity - this.collectible.claimedCount
        );
      },
    },
  };
</script>

<style scoped>
  .collectibleName {
    font-family: 'Work Sans' !important;
    font-style: bold !important;
    font-weight: 700 !important;
    font-size: 38px !important;
    line-height: 48px !important;

    color: #151515;
  }
  .collectibleDescription {
    margin-left: 100px;
    margin-top: 24px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 400 !important;
    font-size: 16px !important;
    line-height: 24px !important;
    letter-spacing: 0.003em !important;

    color: #151515;
  }

  .freeText {
    margin-top: 32px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 700 !important;
    font-size: 24px !important;
    line-height: 32px !important;

    color: #151515;
  }

  .claimButton {
    margin-top: 32px !important;
    margin-right: 0px !important;
    width: 294px !important;
    height: 64px !important;
  }

  .quantityText {
    margin-top: 32px;
    box-sizing: border-box;

    /* Auto layout */

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 24px;
    gap: 24px;

    width: 167px;
    height: 56px;

    background: rgba(235, 235, 235, 0.2);
    /* Colour System 2/Shade/shade-100 */

    border: 1px solid #c0c0c0;
    border-radius: 40px;

    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 500 !important;
    font-size: 16px !important;
    line-height: 32px !important;

    color: #151515;
  }

  .sectionTitle {
    margin-bottom: 36px;
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 700 !important;
    font-size: 45px !important;
    line-height: 52px !important;
    color: #000000;
  }

  .collectibleArtist {
    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 24px !important;
    line-height: 24px !important;
    color: #151515 !important;
  }

  .unselectedPurchaseOption {
    background: #ffe5e7 !important;
    height: 60px !important;
    border-radius: 30px !important;
    text-align: center;
    padding-top: 12px;
    cursor: pointer;

    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 20px !important;
    line-height: 18px !important;
    color: #151515 !important;
  }

  .selectedPurchaseOption {
    background: #414f6f !important;
    height: 60px !important;
    border-radius: 30px !important;
    text-align: center;
    padding-top: 12px;
    cursor: pointer;

    font-family: 'Work Sans' !important;
    font-style: normal !important;
    font-weight: 600 !important;
    font-size: 20px !important;
    line-height: 18px !important;
    color: #fff !important;
  }
</style>
